import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TrendingUp, DollarSign, Target, Clock, Award, Users,
  RefreshCw, CheckCircle, XCircle, AlertTriangle, BarChart2,
  ArrowUpRight, Activity, Globe
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import './DealAnalytics.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const STAGE_LABELS = {
  level_1_assessment:     'L1 Assessment',
  level_2_assessment:     'L2 Assessment',
  loi:                    'LOI',
  final_agreements:       'Final Agreements',
  closed:                 'Closed',
  post_close_integration: 'Post Close',
  deal_closed:            'Deal Closed',
};

const STAGE_COLORS = {
  level_1_assessment:     '#6B7280',
  level_2_assessment:     '#F59E0B',
  loi:                    '#3B82F6',
  final_agreements:       '#8B5CF6',
  closed:                 '#10B981',
  post_close_integration: '#0EA5E9',
  deal_closed:            '#059669',
};

function fmt(v) {
  if (!v && v !== 0) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1,
  }).format(v);
}

function pct(v) {
  return v !== null && v !== undefined ? `${v}%` : '—';
}

// ── Horizontal bar component ───────────────────────────────────────────
function HBar({ label, count, total, color = '#111' }) {
  const width = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="hbar-row">
      <div className="hbar-label">{label}</div>
      <div className="hbar-track">
        <div className="hbar-fill" style={{ width: `${width}%`, background: color }} />
      </div>
      <div className="hbar-count">{count}</div>
    </div>
  );
}

// ── Stat card ──────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, color = '#111' }) {
  return (
    <div className="analytics-stat-card">
      <div className="analytics-stat-icon" style={{ background: `${color}12`, color }}>
        <Icon size={20} />
      </div>
      <div className="analytics-stat-body">
        <div className="analytics-stat-value">{value ?? '—'}</div>
        <div className="analytics-stat-label">{label}</div>
        {sub && <div className="analytics-stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

// ── Pipeline funnel ────────────────────────────────────────────────────
function Funnel({ stages }) {
  const max = Math.max(...stages.map(s => s.count), 1);
  return (
    <div className="funnel-list">
      {stages.map(s => {
        const color = STAGE_COLORS[s.id] || '#6b7280';
        const width = Math.max(Math.round((s.count / max) * 100), s.count > 0 ? 8 : 0);
        return (
          <div key={s.id} className="funnel-row">
            <div className="funnel-label">{STAGE_LABELS[s.id] || s.id}</div>
            <div className="funnel-bar-wrap">
              <div className="funnel-bar" style={{ width: `${width}%`, background: color }} />
            </div>
            <div className="funnel-count" style={{ color }}>{s.count}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────
const DealAnalytics = ({ currentUser }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('overview');
  const [benchmarks, setBenchmarks] = useState(null);

  const isAdmin = currentUser?.role === 'admin';

  const fetchAnalytics = async () => {
    setLoading(true);
    setError('');
    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      const headers = session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
      
      const [analyticsRes, benchRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/deals`, { headers }),
        axios.get(`${API_URL}/api/analytics/benchmarks`, { headers }).catch(() => ({ data: null })),
      ]);
      setData(analyticsRes.data);
      if (benchRes.data?.available) setBenchmarks(benchRes.data);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Could not load analytics. Make sure the backend is running.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAnalytics(); }, []);

  if (loading) {
    return (
      <div className="analytics-page">
        <div className="analytics-loading">
          <div className="analytics-spinner" />
          <p>Loading deal intelligence…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-page">
        <div className="analytics-error">
          <AlertTriangle size={32} />
          <p>{error}</p>
          <button className="analytics-retry-btn" onClick={fetchAnalytics}>Try Again</button>
        </div>
      </div>
    );
  }

  const { summary, funnel, byType, bySource, topIndustries, recentActivity } = data;

  const navItems = [
    { id: 'overview',    label: 'Overview' },
    { id: 'pipeline',    label: 'Pipeline' },
    { id: 'sources',     label: 'Sources & Types' },
    { id: 'timelines',   label: 'Timelines' },
    { id: 'benchmarks',  label: 'Benchmarks' },
    { id: 'activity',    label: 'Activity Feed' },
  ];

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <div className="analytics-title-row">
          <BarChart2 size={22} />
          <h1>Deal Intelligence</h1>
        </div>
        <button className="analytics-refresh-btn" onClick={fetchAnalytics} title="Refresh">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Section nav */}
      <div className="analytics-nav">
        {navItems.map(n => (
          <button
            key={n.id}
            className={`analytics-nav-item ${activeSection === n.id ? 'active' : ''}`}
            onClick={() => setActiveSection(n.id)}
          >
            {n.label}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {activeSection === 'overview' && (
        <div className="analytics-section">
          <div className="analytics-stats-grid">
            <StatCard icon={Target}       label="Total Deals"       value={summary.total}                        color="#6b7280" />
            <StatCard icon={Activity}     label="Active Deals"      value={summary.active}                       color="#3b82f6" />
            <StatCard icon={CheckCircle}  label="Closed Deals"      value={summary.closed}                       color="#10b981" />
            <StatCard icon={XCircle}      label="Discontinued"      value={summary.discontinued}                 color="#ef4444" />
            <StatCard icon={DollarSign}   label="Avg Deal Value"    value={fmt(summary.avgValue)}                color="#8b5cf6" />
            <StatCard icon={DollarSign}   label="Total Pipeline"    value={fmt(summary.totalPipelineValue)}      color="#0ea5e9" />
            <StatCard icon={Award}        label="Win Rate"          value={pct(summary.winRate)}                 color="#10b981"
              sub={summary.winRate !== null ? `${summary.closed} closed / ${summary.closed + summary.discontinued} decided` : 'No decided deals yet'} />
            <StatCard icon={Clock}        label="Avg Days to Close" value={summary.avgDaysToClose ? `${summary.avgDaysToClose}d` : '—'} color="#f59e0b" />
            <StatCard icon={TrendingUp}   label="Avg DD Duration"   value={summary.avgDdDays ? `${summary.avgDdDays}d` : '—'} color="#6b7280" />
            <StatCard icon={Award}        label="Avg Quality Score" value={summary.avgQualityScore ? `${summary.avgQualityScore}/5` : '—'} color="#f59e0b" />
          </div>

          {summary.total === 0 && (
            <div className="analytics-empty">
              <TrendingUp size={48} />
              <h3>No deal data yet</h3>
              <p>Create deals and fill in their details to start seeing intelligence here.</p>
            </div>
          )}
        </div>
      )}

      {/* ── Pipeline ── */}
      {activeSection === 'pipeline' && (
        <div className="analytics-section">
          <div className="analytics-card">
            <div className="analytics-card-title">Pipeline Funnel</div>
            <p className="analytics-card-desc">Number of deals currently in each stage of your pipeline.</p>
            {funnel && funnel.some(s => s.count > 0)
              ? <Funnel stages={funnel} />
              : <p className="analytics-empty-msg">No pipeline data yet.</p>
            }
          </div>

          {topIndustries?.length > 0 && (
            <div className="analytics-card">
              <div className="analytics-card-title">Top Industries</div>
              <div className="hbar-list">
                {topIndustries.map(ind => (
                  <HBar key={ind.name} label={ind.name} count={ind.count}
                    total={summary.total} color="#3b82f6" />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Sources & Types ── */}
      {activeSection === 'sources' && (
        <div className="analytics-section">
          <div className="analytics-two-col">
            <div className="analytics-card">
              <div className="analytics-card-title">Deals by Source</div>
              <p className="analytics-card-desc">Where are your deals coming from?</p>
              {bySource?.length > 0
                ? <div className="hbar-list">
                    {bySource.map(s => (
                      <HBar key={s.name} label={s.name.replace(/_/g,' ')} count={s.count}
                        total={summary.total} color="#8b5cf6" />
                    ))}
                  </div>
                : <p className="analytics-empty-msg">No source data yet. Add deal sources when creating or editing deals.</p>
              }
            </div>

            <div className="analytics-card">
              <div className="analytics-card-title">Deals by Type</div>
              <p className="analytics-card-desc">Deal structure breakdown.</p>
              {byType?.length > 0
                ? <div className="hbar-list">
                    {byType.map(t => (
                      <HBar key={t.name} label={t.name.replace(/_/g,' ')} count={t.count}
                        total={summary.total} color="#0ea5e9" />
                    ))}
                  </div>
                : <p className="analytics-empty-msg">No type data yet. Add deal types when creating or editing deals.</p>
              }
            </div>
          </div>
        </div>
      )}

      {/* ── Timelines ── */}
      {activeSection === 'timelines' && (
        <div className="analytics-section">
          <div className="analytics-card">
            <div className="analytics-card-title">Timeline Benchmarks</div>
            <p className="analytics-card-desc">Average durations across your deal history. Fill in timeline dates on each deal to populate these.</p>
            <div className="analytics-timeline-grid">
              <div className="timeline-metric">
                <div className="timeline-metric-value">
                  {summary.avgDaysToClose ? `${summary.avgDaysToClose}` : '—'}
                </div>
                <div className="timeline-metric-label">Avg days to close</div>
                <div className="timeline-metric-sub">From creation to close</div>
              </div>
              <div className="timeline-metric">
                <div className="timeline-metric-value">
                  {summary.avgDdDays ? `${summary.avgDdDays}` : '—'}
                </div>
                <div className="timeline-metric-label">Avg DD duration</div>
                <div className="timeline-metric-sub">DD start → DD end</div>
              </div>
              <div className="timeline-metric">
                <div className="timeline-metric-value">
                  {summary.avgLoiToCloseDays ? `${summary.avgLoiToCloseDays}` : '—'}
                </div>
                <div className="timeline-metric-label">Avg LOI to close</div>
                <div className="timeline-metric-sub">From LOI → close date</div>
              </div>
            </div>

            {!summary.avgDaysToClose && !summary.avgDdDays && !summary.avgLoiToCloseDays && (
              <div className="analytics-empty-inline">
                <Clock size={28} />
                <p>Fill in timeline dates (NDA, LOI, DD dates) in the Intelligence tab of each deal to see benchmarks here.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Benchmarks ── */}
      {activeSection === 'benchmarks' && (
        <div className="analytics-section">
          <div className="analytics-card">
            <div className="analytics-card-title"><Globe size={16} style={{ display:'inline', marginRight:6 }} />Platform Benchmarks</div>
            <p className="analytics-card-desc">
              Anonymized averages across all DealNexa organizations that have opted in to benchmark sharing.
              Use these to see how your team compares to the market.
            </p>

            {benchmarks ? (
              <>
                <div className="analytics-stats-grid" style={{ marginBottom: '1.5rem' }}>
                  <StatCard icon={CheckCircle} label="Platform Win Rate"     value={pct(benchmarks.winRate)}          color="#10b981" />
                  <StatCard icon={Clock}       label="Avg Days to Close"     value={benchmarks.avgDaysToClose ? `${benchmarks.avgDaysToClose}d` : '—'} color="#f59e0b" />
                  <StatCard icon={TrendingUp}  label="Avg DD Duration"       value={benchmarks.avgDdDays ? `${benchmarks.avgDdDays}d` : '—'}        color="#6b7280" />
                  <StatCard icon={Award}       label="Avg Quality Score"     value={benchmarks.avgQualityScore ? `${parseFloat(benchmarks.avgQualityScore).toFixed(1)}/5` : '—'} color="#8b5cf6" />
                </div>

                {benchmarks.topSources?.length > 0 && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#888', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Top Deal Sources (Platform)</div>
                    <div className="hbar-list">
                      {benchmarks.topSources.map(s => (
                        <HBar key={s.name} label={s.name.replace(/_/g,' ')} count={s.count} total={benchmarks.totalDeals} color="#8b5cf6" />
                      ))}
                    </div>
                  </div>
                )}

                <p className="analytics-empty-msg" style={{ marginTop: '0.5rem' }}>
                  Based on {benchmarks.totalDeals} deals across the platform. Updated daily. Data is fully anonymized — no org-identifying information is shared.
                </p>
              </>
            ) : (
              <div className="analytics-empty-inline">
                <Globe size={32} />
                <p>Platform benchmarks are not yet available. They appear once enough anonymized data has been collected. You can enable benchmark sharing in <strong>Settings → Notifications</strong>.</p>
              </div>
            )}
          </div>

          {summary.total > 0 && benchmarks && (
            <div className="analytics-card">
              <div className="analytics-card-title">Your Team vs Platform</div>
              <div className="analytics-compare-grid">
                {[
                  { label: 'Win Rate',        yours: summary.winRate !== null ? `${summary.winRate}%` : '—',                     platform: pct(benchmarks.winRate) },
                  { label: 'Days to Close',   yours: summary.avgDaysToClose ? `${summary.avgDaysToClose}d` : '—',                platform: benchmarks.avgDaysToClose ? `${benchmarks.avgDaysToClose}d` : '—' },
                  { label: 'DD Duration',     yours: summary.avgDdDays ? `${summary.avgDdDays}d` : '—',                         platform: benchmarks.avgDdDays ? `${benchmarks.avgDdDays}d` : '—' },
                  { label: 'Quality Score',   yours: summary.avgQualityScore ? `${summary.avgQualityScore}/5` : '—',             platform: benchmarks.avgQualityScore ? `${parseFloat(benchmarks.avgQualityScore).toFixed(1)}/5` : '—' },
                ].map(row => (
                  <div key={row.label} className="compare-row">
                    <span className="compare-label">{row.label}</span>
                    <span className="compare-yours">{row.yours} <small>You</small></span>
                    <span className="compare-platform">{row.platform} <small>Platform</small></span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Activity Feed ── */}
      {activeSection === 'activity' && (
        <div className="analytics-section">
          <div className="analytics-card">
            <div className="analytics-card-title">Recent Activity</div>
            <p className="analytics-card-desc">Latest actions across your deal platform.</p>
            {recentActivity?.length > 0 ? (
              <div className="activity-feed">
                {recentActivity.map(event => (
                  <div key={event.id} className="activity-item">
                    <div className="activity-dot" />
                    <div className="activity-body">
                      <span className="activity-title">{event.title}</span>
                      <span className="activity-time">
                        {new Date(event.timestamp).toLocaleString('en-US', {
                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="analytics-empty-msg">No activity recorded yet.</p>
            )}
          </div>
        </div>
      )}

      {/* Data quality nudge */}
      {summary.total > 0 && (
        <div className="analytics-data-quality">
          <ArrowUpRight size={14} />
          <span>
            <strong>Improve your intelligence:</strong> Add deal type, source, geography, and timeline dates to each deal for richer analytics.
          </span>
        </div>
      )}
    </div>
  );
};

export default DealAnalytics;
